<template>
  <BModal
    :model-value="modal"
    scrollable
    centered
    title="Extend"
    @update:modelValue="updateModal"
  >
    <template #header>
      <div
        class="d-flex align-items-center justify-content-between modal-title w-100"
      >
        <h5 class="">Extend Form</h5>
      </div>
      <BButton variant="link" class="close" @click="updateModal(false)">
        <i class="ion ion-android-close"></i>
      </BButton>
    </template>

    <BTabs content-class="mt-3" align="center" justified>
      <!-- TAB 1: Extend Stay -->
      <BTab title="Extend Stay">
        <BForm>
          <BRow>
            <BCol>
              <label for="">Current Check-out Date</label>
              <BFormInput
                type="text"
                :model-value="displayStartDate"
                disabled
              />
            </BCol>
          </BRow>
          <br>
          <BRow>
            <BCol>
              <label for="">Select New Check-out Date</label>
              <BFormInput 
                type="date" 
                v-model="postData.end"
                :min="minEndDate"
              />
            </BCol>
          </BRow>
          <br>
          <BRow class="form-group">
            <BCol>
              <BFormSelect
                v-model="postData.room_category"
                :options="optionRoomCategory"
                label="Room Category"
                text-field="text"
                value-field="value"
              >
                <template #first>
                  <BFormSelectOption :value="null" disabled>
                    Select Room Category
                  </BFormSelectOption>
                </template>
              </BFormSelect>
            </BCol>
          </BRow>

          <BRow class="form-group">
            <BCol>
              <BFormSelect
                v-model="postData.room_uuid_change"
                :options="optionRoomItems"
                text-field="code"
                value-field="uuid"
              >
                <template #first>
                  <BFormSelectOption :value="null" disabled>
                    Select Room Number
                  </BFormSelectOption>
                </template>
              </BFormSelect>
            </BCol>
          </BRow>

          <div class="form-group">
            <BFormTextarea
              type="text"
              v-model="postData.description"
              placeholder="Remark (optional)"
            />
          </div>

          <div class="card bg-light p-2 mt-2">
            <div class="d-flex justify-content-between mb-1">
              <span>Additional Nights:</span>
              <strong>{{ calculateDays }} night(s)</strong>
            </div>
            <div class="d-flex justify-content-between mb-1">
              <span>Rate / Night:</span>
              <strong>{{ formatIDR(currentRoomPrice) }}</strong>
            </div>
            <div class="d-flex justify-content-between">
              <span class="text-primary fw-bold">Estimated Extension Cost:</span>
              <strong class="text-primary">{{ formatIDR(calculateDays * currentRoomPrice) }}</strong>
            </div>
          </div>
        </BForm>
      </BTab>

      <!-- TAB 2: Payment -->
      <BTab title="Payment">
        <BForm>
          <div class="form-group mb-3">
            <label>Payment Method</label>
            <BFormSelect v-model="paymentForm.payment_method_uuid">
              <template #first>
                <BFormSelectOption :value="null">
                  Select payment method (Optional)
                </BFormSelectOption>
              </template>
              <BFormSelectOption
                v-for="method in paymentMethods"
                :key="method.uuid"
                :value="method.uuid"
              >
                {{ method.name }} {{ method.number ? `- ${method.number}` : '' }}
              </BFormSelectOption>
            </BFormSelect>
          </div>

          <div class="form-group mb-3">
            <div class="d-flex justify-content-between">
              <label>Amount to Pay</label>
              <span class="text-muted small">Estimated: {{ formatIDR(calculateDays * currentRoomPrice) }}</span>
            </div>
            <BFormInput
              type="number"
              v-model.number="paymentForm.amount"
              placeholder="Nominal amount to be paid"
              min="0"
            />
          </div>

          <div class="form-group mb-3">
            <label>Payment Date</label>
            <BFormInput
              type="date"
              v-model="paymentForm.payment_date"
            />
          </div>

          <div class="form-group mb-3">
            <label>Payment Remarks</label>
            <BFormTextarea
              v-model="paymentForm.payment_description"
              placeholder="Remarks for payment (Optional)"
              rows="3"
            />
          </div>

          <div v-if="paymentForm.amount > 0" class="alert alert-info py-2 px-3 small">
            Payment of <strong>{{ formatIDR(paymentForm.amount) }}</strong> will be recorded upon saving extension.
          </div>
        </BForm>
      </BTab>
    </BTabs>

    <template #footer>
      <div class="d-flex justify-content-end">
        <BButton
          variant="primary"
          @click="createExtend()"
          class="mr-2"
          :disabled="calculateDays < 1 || isSubmitting"
        >
          {{ isSubmitting ? 'Saving...' : 'Save' }}
        </BButton>
        <BButton variant="danger" @click="updateModal(false)">Cancel</BButton>
      </div>
    </template>
  </BModal>

  <ToastResponse
    v-model="modalResponse.toast"
    :text="modalResponse.text"
    :success="modalResponse.success"
  />
</template>

<script>
import {
  BModal,
  BFormInput,
  BButton,
  BForm,
  BFormTextarea,
  BFormSelect,
  BFormSelectOption,
  BRow,
  BCol,
  BTabs,
  BTab,
} from "bootstrap-vue-next";
import { computed, ref, reactive, inject, watch, onMounted } from "vue";
import ToastResponse from "../ToastResponse.vue";
import { formatIDR } from "@/utils/currency";
import { getFormattedDate } from "@/composable/getDateTimeFormatter";

export default {
  components: {
    BModal,
    BRow,
    BFormInput,
    BButton,
    BForm,
    BFormTextarea,
    BFormSelect,
    BFormSelectOption,
    BCol,
    BTabs,
    BTab,
    ToastResponse,
  },

  props: {
    modal: Boolean,
    item: Object,
  },
  emits: ["update:modal", "addExtend", "addOrUpdate", "extendSuccess"],
  
  setup(props, { emit }) {
    const repositories = inject("repositories");
    const roomCategoryRepository = repositories ? repositories.roomCategory : null;
    const roomRepository = repositories ? repositories.rooms : null;
    const createExtendRepository = repositories ? repositories.inhouse : null;
    const paymentMethodRepository = repositories ? repositories.paymentMethod : null;

    const isSubmitting = ref(false);
    const paymentMethods = ref([]);

    const postData = reactive({
      reservation_uuid: null,
      room_uuid: null,
      room_price: null,
      room_category: null,
      room_uuid_change: null,
      start: null,
      end: '',
      days: '',
      description: '',
    });

    const paymentForm = reactive({
      payment_method_uuid: null,
      amount: 0,
      payment_date: new Date().toISOString().split('T')[0],
      payment_description: '',
    });
    
    const rooms = ref([]);
    const optionRoomCategory = ref([]);

    // Helper untuk mengambil tanggal murni format YYYY-MM-DD
    const normalizeDateStr = (dateVal) => {
      if (!dateVal) return '';
      if (typeof dateVal === 'string' && dateVal.includes('T')) {
        return dateVal.split('T')[0];
      }
      if (typeof dateVal === 'string' && dateVal.includes(' ')) {
        return dateVal.split(' ')[0];
      }
      const d = new Date(dateVal);
      if (isNaN(d.getTime())) return '';
      return d.toISOString().split('T')[0];
    };

    const fillForm = () => {
      if (!props.item) return;

      const rawItem = props.item.item || props.item;
      const res = rawItem.reservation || {};
      const room = res.room || rawItem.room || {};
      const roomCat = room.room_category || {};

      postData.reservation_uuid = rawItem.reservation_uuid || res.uuid;
      postData.room_uuid = room.uuid || null;
      postData.room_price = Number(res.room_price ?? roomCat.low_season_price ?? 0);
      postData.room_category = roomCat.name || null;

      const rawCheckOut = rawItem.check_out_date || res.end;
      postData.start = normalizeDateStr(rawCheckOut);
      postData.end = '';
      postData.days = '';
      postData.description = '';

      paymentForm.payment_method_uuid = null;
      paymentForm.amount = 0;
      paymentForm.payment_date = new Date().toISOString().split('T')[0];
      paymentForm.payment_description = '';
    };

    const displayStartDate = computed(() => {
      if (!postData.start) return '-';
      try {
        return getFormattedDate(postData.start);
      } catch (e) {
        return postData.start;
      }
    });

    const modalResponse = ref({
      toast: false,
      text: "",
      success: false,
    });

    const minEndDate = computed(() => {
      if (postData.start) {
        const parts = postData.start.split('-');
        if (parts.length === 3) {
          const start = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
          start.setDate(start.getDate() + 1);
          const y = start.getFullYear();
          const m = String(start.getMonth() + 1).padStart(2, '0');
          const d = String(start.getDate()).padStart(2, '0');
          return `${y}-${m}-${d}`;
        }
      }
      return '';
    });

    const calculateDays = computed(() => {
      if (postData.start && postData.end) {
        const startParts = postData.start.split('-');
        const endParts = postData.end.split('-');
        if (startParts.length === 3 && endParts.length === 3) {
          const startDate = new Date(Number(startParts[0]), Number(startParts[1]) - 1, Number(startParts[2]));
          const endDate = new Date(Number(endParts[0]), Number(endParts[1]) - 1, Number(endParts[2]));
          const diffMs = endDate.getTime() - startDate.getTime();
          const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
          return diffDays > 0 ? diffDays : 0;
        }
      }
      return 0;
    });

    const isHighSeason = computed(() => {
      const highSeasonMonths = [5, 6, 7, 8, 11]; // Juni–Desember
      if (!postData.start || !postData.end) return false;

      const startParts = postData.start.split('-');
      const endParts = postData.end.split('-');
      if (startParts.length !== 3 || endParts.length !== 3) return false;

      const current = new Date(Number(startParts[0]), Number(startParts[1]) - 1, Number(startParts[2]));
      const end = new Date(Number(endParts[0]), Number(endParts[1]) - 1, Number(endParts[2]));

      while (current < end) {
        if (highSeasonMonths.includes(current.getMonth())) {
          return true;
        }
        current.setDate(current.getDate() + 1);
      }
      return false;
    });

    const currentRoomPrice = computed(() => {
      // 1. Cek dari daftar kamar yang sedang dipilih/tersedia
      const selectedRoom = rooms.value.find(
        (room) => room.uuid === (postData.room_uuid_change || postData.room_uuid)
      );

      if (selectedRoom && selectedRoom.room_category) {
        const priceType = isHighSeason.value ? "high_season_price" : "low_season_price";
        const catPrice = selectedRoom.room_category[priceType];
        if (catPrice && Number(catPrice) > 0) return Number(catPrice);

        // Jika harga tersimpan di prices array
        if (Array.isArray(selectedRoom.room_category.prices) && selectedRoom.room_category.prices.length) {
          const season = isHighSeason.value ? 'high' : 'low';
          const p = selectedRoom.room_category.prices.find(item => item.season === season);
          if (p && Number(p.price) > 0) return Number(p.price);
          return Number(selectedRoom.room_category.prices[0].price || 0);
        }
      }

      // 2. Cek dari props item awal
      const rawItem = props.item?.item || props.item;
      const res = rawItem?.reservation || {};
      const room = res.room || rawItem?.room || {};
      const cat = room.room_category;

      if (cat) {
        const priceType = isHighSeason.value ? "high_season_price" : "low_season_price";
        if (cat[priceType] && Number(cat[priceType]) > 0) return Number(cat[priceType]);

        if (Array.isArray(cat.prices) && cat.prices.length) {
          const season = isHighSeason.value ? 'high' : 'low';
          const p = cat.prices.find(item => item.season === season);
          if (p && Number(p.price) > 0) return Number(p.price);
          return Number(cat.prices[0].price || 0);
        }
      }

      // 3. Fallback ke harga per malam reservasi (room_price)
      if (res.room_price && Number(res.room_price) > 0) {
        return Number(res.room_price);
      }

      return Number(postData.room_price) || 0;
    });

    // Auto update payment amount when calculateDays changes
    watch([calculateDays, currentRoomPrice], ([days, price]) => {
      if (days > 0 && price > 0) {
        paymentForm.amount = days * price;
      } else {
        paymentForm.amount = 0;
      }
    });

    const getRoomCategoryList = async () => {
      try {
        const response = await roomCategoryRepository.getAll();
        optionRoomCategory.value = response.data.data.map(item => ({
          value: item.name,
          text: item.name,
        }));
      } catch (e) {
        console.log(e);
      }
    };

    const getRoomList = (category) => {
      const rawItem = props.item?.item || props.item;
      roomRepository
        .getAll({
          category: category,
          room_uuid: rawItem?.reservation?.room?.uuid || rawItem?.room?.uuid
        })
        .then((res) => {
          rooms.value = res.data.data;
        })
        .catch((err) => {
          console.error(err);
        });
    };

    const optionRoomItems = computed(() => {
      return rooms.value.filter((room) => 
        room.room_category.name === postData.room_category &&
        (room.status === 'available' || room.uuid === postData.room_uuid)
      );
    });

    const getPaymentMethods = async () => {
      if (!paymentMethodRepository) return;
      try {
        const res = await paymentMethodRepository.getAll();
        paymentMethods.value = res.data.data.filter((i) => i.status === "active");
      } catch (err) {
        console.error("Error loading payment methods:", err);
      }
    };

    const updateModal = (value) => {
      emit("update:modal", value);
    };

    const createExtend = async () => {
      if (calculateDays.value < 1) return;

      postData.days = calculateDays.value;
      postData.room_price = currentRoomPrice.value;
      postData.description = postData.description === null || postData.description === '' ? '-' : postData.description;

      const payload = {
        ...postData,
        payment_method_uuid: paymentForm.payment_method_uuid || null,
        amount: paymentForm.amount ? Number(paymentForm.amount) : 0,
        payment_date: paymentForm.payment_date || null,
        payment_description: paymentForm.payment_description || null,
      };

      try {
        isSubmitting.value = true;
        const updateData = await createExtendRepository.extend(postData.reservation_uuid, payload);

        updateModal(false);
        emit("addExtend");
        emit("addOrUpdate");
        emit("extendSuccess", {
          item: props.item,
          extend_details: updateData.data?.extend_details || {
            additional_days: postData.days,
            room_price: postData.room_price,
            extend_cost: postData.days * postData.room_price,
            payment_amount: payload.amount,
            new_end_date: postData.end,
          }
        });

        modalResponse.value = {
          toast: true,
          text: updateData.data.message || 'Create Extend Successfully',
          success: true,
        };
      } catch (e) {
        console.log('ERROR DETAIL:', e);
        modalResponse.value = {
          toast: true,
          text: e.response?.data?.message || 'Process Extend failed!',
          success: false,
        };
      } finally {
        isSubmitting.value = false;
      }
    };

    watch(() => props.item, (val) => {
      if (val) {
        fillForm();
        const raw = val.item || val;
        const catName = raw.reservation?.room?.room_category?.name || raw.room?.room_category?.name;
        if (catName) {
          getRoomList(catName);
        }
      }
    }, { immediate: true });

    watch(() => postData.room_category, (val) => {
      if (val) getRoomList(val);
      postData.room_uuid_change = null;
      rooms.value = [];
    });

    onMounted(() => {
      getRoomCategoryList();
      getPaymentMethods();
      if (props.item) {
        fillForm();
      }
    });

    return {
      updateModal,
      getRoomCategoryList,
      getRoomList,
      postData,
      paymentForm,
      paymentMethods,
      optionRoomCategory,
      optionRoomItems,
      createExtend,
      modalResponse,
      calculateDays,
      minEndDate,
      currentRoomPrice,
      formatIDR,
      displayStartDate,
      isSubmitting,
    };
  },
};
</script>

